import { getStaffSessionToken } from '../modules/auth/staffSession';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { authSelector } from '../modules/auth/state/authSlice';
import { useGenerateShareLinkMutation } from '../modules/auth/api/authApi';
import { useGetUserDataQuery } from '../modules/auth/api/authApi';

export const AdminShareButton = () => {
  const { isAdmin, token } = useSelector(authSelector);
  const { pathname } = useLocation();
  const [generateShareLink, { isLoading }] = useGenerateShareLinkMutation();
  const { data: userData } = useGetUserDataQuery({ token: token || '' }, { skip: !token });
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  // Only show for admin users
  if (!isAdmin) return null;

  const userEmail = userData?.data?.email;

  const handleShare = async () => {
    if (!userEmail) {
      toast('User email not found', { type: 'error' });
      return;
    }

    // Get stored admin password from session
    const staffSession = getStaffSessionToken();
    if (!staffSession) {
      toast('Admin session expired, please login again', { type: 'error' });
      return;
    }

    try {
      const res = await generateShareLink({
        email: userEmail,

        redirect: pathname
      }).unwrap();

      if (res?.magicLink) {
        setGeneratedLink(res.magicLink);
        await navigator.clipboard.writeText(res.magicLink);
        toast('Share link copied to clipboard!', { type: 'success' });
      }
    } catch (err) {
      toast('Failed to generate share link', { type: 'error' });
    }
  };

  const copyLink = async () => {
    if (generatedLink) {
      await navigator.clipboard.writeText(generatedLink);
      toast('Copied!', { type: 'success' });
    }
  };

  return (
    <>
      <ShareBtn onClick={handleShare} disabled={isLoading}>
        {isLoading ? '⏳' : '🔗'} Share Page
      </ShareBtn>

      {generatedLink && (
        <LinkPopup>
          <LinkText onClick={copyLink} title="Click to copy">
            {generatedLink}
          </LinkText>
          <CloseBtn onClick={() => setGeneratedLink(null)}>×</CloseBtn>
        </LinkPopup>
      )}
    </>
  );
};

const ShareBtn = styled.button<{ disabled?: boolean }>`
  position: fixed;
  top: 92px;
  right: 10px;
  z-index: 9999;
  background-color: ${props => props.disabled ? '#ccc' : '#29a8d3'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: ${props => props.disabled ? '#ccc' : '#1e8fb3'};
  }
`;

const LinkPopup = styled.div`
  position: fixed;
  top: 132px;
  right: 10px;
  z-index: 9999;
  background: #e8f5e9;
  border: 1px solid #4caf50;
  border-radius: 8px;
  padding: 12px;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const LinkText = styled.div`
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
  cursor: pointer;
  flex: 1;

  &:hover {
    text-decoration: underline;
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 0;
  line-height: 1;

  &:hover {
    color: #333;
  }
`;
